import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import {
    setRetireBirthDate, setRetireTargetAge, calculateRetirement, clearRetirement,
    setServiceCurrentAge, setServiceYearsWorked, setServiceRuleTarget, calculateServiceRule, clearServiceRule,
} from '../store/slices/ageSlice'
import '../pages/AgePage.css'

// Static reference data — always rendered regardless of which calculator
// mode is active, so react-snap's single prerendered snapshot always
// contains it (avoids the hydration/indexing gap conditional tab content
// would create).
const COUNTRY_RETIREMENT_DATA = [
    {
        country: 'Pakistan (Government employees)',
        age: '60 years',
        note: 'Federal and provincial civil servants retire at 60. Pension reforms have been discussed alongside recent budget talks, but no change to the standard retirement age has been officially notified.',
    },
    {
        country: 'India (Central Government employees)',
        age: '60 years',
        note: 'No proposal to change the standard retirement age for central government employees is currently under consideration, per official statements.',
    },
    {
        country: 'United Kingdom (State Pension age)',
        age: '66, rising to 67',
        note: 'Phasing from 66 to 67 between April 2026 and April 2028 for anyone born on or after 6 April 1960. A further rise to 68 is legislated for 2044–2046.',
    },
    {
        country: 'United States (Social Security Full Retirement Age)',
        age: '66–67 (by birth year)',
        note: 'FRA is 66 for people born 1943–1954, rising in steps to 67 for anyone born 1960 or later. Early claiming is possible from age 62 (reduced benefit); delaying to age 70 increases the monthly benefit.',
    },
]

const RETIREMENT_FAQS = [
    {
        q: 'What is the retirement age in the US?',
        a: 'For Social Security, Full Retirement Age (FRA) is 67 for anyone born in 1960 or later. Early retirement is available from 62 at a reduced benefit; delayed retirement credits accrue up to age 70.',
    },
    {
        q: 'What is the UK State Pension age?',
        a: 'The UK State Pension age is currently 66, rising to 67 between 2026 and 2028, then 68 by 2044–2046. The age applies to both men and women born on or after 6 April 1960.',
    },
    {
        q: 'How do I calculate my retirement date?',
        a: 'Add your official retirement age to your date of birth. Example: born 15 March 1970 with a retirement age of 67 gives a retirement date of 15 March 2037.',
    },
    {
        q: 'What is the Rule of 80 for retirement?',
        a: 'The Rule of 80 means you become eligible when your age plus years of service total 80. A 52-year-old with 28 years of service qualifies immediately. Some pension schemes use 85 or 90 instead.',
    },
    {
        q: 'Is the retirement age the same in Pakistan and India?',
        a: 'Yes. Both Pakistan and India set the standard retirement age for central and federal government employees at 60 years. Private-sector retirement ages in both countries vary by employer.',
    },
    {
        q: 'Is the UK retirement age changing in 2026?',
        a: 'Yes. The UK State Pension age is rising from 66 to 67, phased in between April 2026 and April 2028, affecting anyone born on or after 6 April 1960. A further rise to 68 is planned for 2044–2046.',
    },
    {
        q: 'Was the UK retirement age different for women in the past?',
        a: "Yes. The UK State Pension age for women used to be 60, compared to 65 for men. It was equalised to 65 by November 2018, then raised to 66 by October 2020. Today both men and women share the same State Pension age.",
    },
    {
        q: 'Can I retire before my official retirement age?',
        a: 'In most systems, yes — but with reduced benefits. In the US, you can claim Social Security as early as 62, but your monthly benefit is permanently lower than if you wait until full retirement age.',
    },
    {
        q: 'What is the difference between retirement age and pension eligibility age?',
        a: "Retirement age is when you're allowed (or required) to stop working under your employer's rules. Pension eligibility age is when you can start receiving pension payments — these are sometimes the same, but not always, especially in service-based schemes like the Rule of 80.",
    },
    {
        q: 'Does the calculator account for my specific pension scheme?',
        a: "No. This is a general estimator based on standard age or age-plus-service formulas. Pension amounts, commutation, and exact eligibility can vary by department, employer, and country. Always verify final figures with your official pension office.",
    },
    {
        q: 'Can I calculate retirement age for me and my spouse together?',
        a: "Not directly in this version. Run the calculator separately for each date of birth to compare retirement timelines. We're considering a joint/couples mode based on user demand.",
    },
    {
        q: 'Is my personal information stored when I use this calculator?',
        a: 'No. All calculations run in your browser using JavaScript. Your birth date is never sent to a server, logged, or stored anywhere.',
    },
]

const RetirementAgeCalculatorPage = ({ tools = [] }) => {
    const dispatch = useDispatch()
    const {
        retireBirthDate, retireTargetAge, retireResult,
        serviceCurrentAge, serviceYearsWorked, serviceRuleTarget, serviceRuleResult,
    } = useSelector(state => state.age)
    const [lastUpdated, setLastUpdated] = useState(null)

    // Only the calculator widget (inputs + result) switches with this tab.
    // Everything below it — guide, reference table, FAQ, related tools —
    // stays permanently rendered so it's always present in the prerendered
    // HTML react-snap generates, regardless of which mode is selected.
    const [activeMode, setActiveMode] = useState('birthYear')

    useEffect(() => {
        setLastUpdated(
            new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        )
    }, [])



    return (
        <ToolPageTemplate path="/retirement-age-calculator/" tools={tools} >
            <div>

                <p className="tool-intro">
                    Retirement age isn't a single fixed number. It depends on where you work, whether you're in
                    government or private service, and — for many pension schemes — how many years you've actually
                    served, not just how old you are. Pick the mode below that matches how your employer or country
                    calculates eligibility.
                </p>

                <section className="age-calculator">
                    <div className="age-tabs">
                        <button
                            type="button"
                            className={`age-tab ${activeMode === 'birthYear' ? 'active' : ''}`}
                            onClick={() => setActiveMode('birthYear')}
                        >
                            By Birth Year
                        </button>
                        <button
                            type="button"
                            className={`age-tab ${activeMode === 'service' ? 'active' : ''}`}
                            onClick={() => setActiveMode('service')}
                        >
                            By Years of Service
                        </button>
                    </div>

                    {activeMode === 'birthYear' ? (
                        <>
                            <div className="age-input-section">
                                <Input
                                    type="date"
                                    label="Date of Birth"
                                    value={retireBirthDate}
                                    onChange={(e) => dispatch(setRetireBirthDate(e.target.value))}
                                    max={new Date().toISOString().split('T')[0]}
                                />
                                <Input
                                    type="number"
                                    label="Target Retirement Age"
                                    value={retireTargetAge}
                                    onChange={(e) => dispatch(setRetireTargetAge(e.target.value))}
                                    min="40"
                                    max="80"
                                />
                                <div className="age-buttons">
                                    <Button onClick={() => dispatch(calculateRetirement())} disabled={!retireBirthDate}>Calculate Retirement Date</Button>
                                    <Button variant="secondary" onClick={() => dispatch(clearRetirement())}>Clear</Button>
                                </div>
                            </div>

                            {retireResult && (
                                <ResultBox title="Retirement Estimate">
                                    <div className="age-details">
                                        <div className="age-item">
                                            <span className="age-label">Retirement Date:</span>
                                            <span className="age-value">{retireResult.retirementDate}</span>
                                        </div>
                                        <div className="age-item">
                                            <span className="age-label">{retireResult.isPast ? 'Time Since Retirement:' : 'Time Remaining:'}</span>
                                            <span className="age-value">{retireResult.years}y {retireResult.months}m</span>
                                        </div>
                                    </div>
                                </ResultBox>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="age-input-section">
                                <Input
                                    type="number"
                                    label="Your Current Age"
                                    value={serviceCurrentAge}
                                    onChange={(e) => dispatch(setServiceCurrentAge(e.target.value))}
                                    min="18"
                                    max="80"
                                />
                                <Input
                                    type="number"
                                    label="Years of Service Completed"
                                    value={serviceYearsWorked}
                                    onChange={(e) => dispatch(setServiceYearsWorked(e.target.value))}
                                    min="0"
                                    max="60"
                                />
                                <label>
                                    Pension Scheme Target
                                    <select
                                        value={serviceRuleTarget}
                                        onChange={(e) => dispatch(setServiceRuleTarget(e.target.value))}
                                    >
                                        <option value="80">Rule of 80</option>
                                        <option value="85">Rule of 85</option>
                                        <option value="90">Rule of 90</option>
                                    </select>
                                </label>
                                <div className="age-buttons">
                                    <Button onClick={() => dispatch(calculateServiceRule())} disabled={!serviceCurrentAge || serviceYearsWorked === ''}>Check Eligibility</Button>
                                    <Button variant="secondary" onClick={() => dispatch(clearServiceRule())}>Clear</Button>
                                </div>
                            </div>

                            {serviceRuleResult && (
                                <ResultBox title={`Rule of ${serviceRuleResult.ruleTarget} Result`}>
                                    <div className="age-details">
                                        <div className="age-item">
                                            <span className="age-label">Current Combined Score:</span>
                                            <span className="age-value">{serviceRuleResult.currentSum} / {serviceRuleResult.ruleTarget}</span>
                                        </div>
                                        <div className="age-item">
                                            <span className="age-label">Status:</span>
                                            <span className="age-value">{serviceRuleResult.eligible ? 'Eligible Now' : 'Not Yet Eligible'}</span>
                                        </div>
                                        {!serviceRuleResult.eligible && (
                                            <>
                                                <div className="age-item">
                                                    <span className="age-label">Years Until Eligible:</span>
                                                    <span className="age-value">{serviceRuleResult.yearsUntilEligible}</span>
                                                </div>
                                                <div className="age-item">
                                                    <span className="age-label">Eligible At Age:</span>
                                                    <span className="age-value">{serviceRuleResult.eligibleAge} (with {serviceRuleResult.eligibleServiceYears} years of service)</span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </ResultBox>
                            )}
                        </>
                    )}
                </section>

                <section className="tool-guide">
                    <div className="guide-section">
                        <h2>How to Use the Retirement Age Calculator</h2>
                        <div className="guide-steps">
                            <div className="step">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <h3>By Birth Year</h3>
                                    <p>Enter your date of birth and your target retirement age, and the calculator instantly shows your projected retirement date along with how much time remains — or has passed, if your target age is already behind you.</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <h3>By Years of Service (Rule of 80/85/90)</h3>
                                    <p>Many government and public-sector pension schemes retire you once your age plus years of service reach a set number, commonly 80, 85, or 90. Enter your current age and years already served, choose your scheme's target, and see whether you're eligible now — or exactly how many years until you are.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="guide-section">
                        <h2>Why Use This Tool</h2>
                        <p>Planning for retirement starts with knowing exactly when it will happen. Whether you're budgeting for pension contributions, planning savings milestones, or checking eligibility under an age-plus-service pension scheme, this calculator gives you a clear, instant estimate — no manual math required.</p>
                        <p>Looking for something broader than retirement? This tool is specifically for working out <strong>when you can retire</strong> — your pension or career end date. If you want to check age eligibility for other life milestones instead (driving, voting, senior discounts, and more), use our <Link to="/eligibility-calculator/">Eligibility Calculator</Link> instead.</p>
                    </div>

                    <div className="guide-section">
                        <h2>Retirement Age by Country (2026)</h2>
                        <p>Standard retirement age varies significantly by country and by whether you work in government or private service. Here's a quick reference for common systems:</p>
                        <div className="retirement-country-table-wrap">
                            <table className="retirement-country-table">
                                <thead>
                                    <tr>
                                        <th>Country / System</th>
                                        <th>Standard Retirement Age</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {COUNTRY_RETIREMENT_DATA.map((row) => (
                                        <tr key={row.country}>
                                            <td>{row.country}</td>
                                            <td>{row.age}</td>
                                            <td>{row.note}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="table-trust-note">
                            Private-sector and employer-specific retirement ages often differ from these government figures.
                            Always confirm your exact eligibility date with your employer's HR department or official
                            pension authority — this table is a general reference, not a substitute for official confirmation.
                        </p>
                    </div>

                    <div className="guide-section">
                        <h2>Frequently Asked Questions</h2>
                        <div className="faq-list">
                            {RETIREMENT_FAQS.map((item) => (
                                <div className="faq-item" key={item.q}>
                                    <h3>{item.q}</h3>
                                    <p>{item.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="tool-guide">
                    <div className="guide-section">
                        <h2>Related Tools</h2>
                        <div className="related-tools">
                            <Link to="/age/" className="related-tool">
                                <div className="tool-icon">Age Calculator</div>
                                <div className="tool-description">Calculate your exact age from date of birth</div>
                            </Link>
                            <Link to="/eligibility-calculator/" className="related-tool">
                                <div className="tool-icon">Eligibility Calculator</div>
                                <div className="tool-description">Check exam and milestone age eligibility</div>
                            </Link>
                            <Link to="/generation-calculator/" className="related-tool">
                                <div className="tool-icon">Generation Calculator</div>
                                <div className="tool-description">Find out which generation you belong to</div>
                            </Link>
                            <Link to="/blog/retirement-age-by-birth-year-chart/" className="related-tool">
                                <div className="tool-icon">Retirement Age by Birth Year Chart</div>
                                <div className="tool-description">Find your exact Full Retirement Age based on your birth year</div>
                            </Link>
                        </div>
                    </div>
                </section>

                <section className="cta-banner">
                    <h2>Ready to Plan Ahead?</h2>
                    <p>Try both calculation modes above to see your retirement date from every angle — by birth year and by years of service — so you're not caught off guard by a scheme you didn't know applied to you.</p>
                </section>

                <div className="last-updated">
                    <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
                </div>
            </div>
        </ToolPageTemplate>
    )
}

export default RetirementAgeCalculatorPage